    // src/pages/Login.js
    import React, { useState, useEffect } from "react";
    import { useLocation, useNavigate } from "react-router-dom";
    import axios from "axios";
    import { LogIn, AlertCircle, Loader } from 'lucide-react'; // Import icons
    import logo from "../assets/images/apemlogo.png"; // Assuming logo is suitable for dark bg
    import { forgetShowSession } from '../lib/showSession';

    // --- Helper: Inject CSS Keyframes for Spinners ---
    if (!document.getElementById('spinner-keyframes')) {
        const keyframes = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        `;
        const styleSheet = document.createElement("style");
        styleSheet.id = 'spinner-keyframes';
        styleSheet.type = "text/css";
        styleSheet.innerText = keyframes;
        document.head.appendChild(styleSheet);
    }
    // --- End Helper ---


    export default function Login() {
        const [email, setEmail] = useState("");
        const [password, setPassword] = useState("");
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(null); // State for login errors
        const navigate = useNavigate();
        const location = useLocation();
        const returnPath = location.state?.from
            ? `${location.state.from.pathname || '/home'}${location.state.from.search || ''}`
            : '/home';

        // Redirect if already logged in
        useEffect(() => {
            const token = localStorage.getItem("token");
            if (token) {
                console.log("Login: Token found, redirecting to /home");
                navigate(returnPath, { replace: true }); // Return to the page that required login.
            }
        }, [navigate, returnPath]);

        // Handle Login Submission
        const handleLogin = async () => {
            setError(null); // Clear previous errors
            // Basic validation
            if (!email || !password) {
                setError("Por favor, preencha seu e-mail e senha.");
                return;
            }
            if (!/\S+@\S+\.\S+/.test(email)) {
                setError("Formato de e-mail inválido.");
                return;
            }

            setLoading(true);
            console.log("Login: Attempting login for:", email);

            try {
                // Send only lowercase email/password ? Ensure backend expects this
                const response = await axios.post("https://apem.riderup.uk/api/login", {
                    email: email.toLowerCase().trim(), // Send trimmed and lowercase
                    password: password, // Send password as typed
                });

                console.log("Login: API Response Status:", response.status);
                console.log("Login: API Response Data:", response.data);

                // Check for successful login based on your API's response
                if (response.status === 200 && response.data.token) {
                    console.log("Login: Success! Storing token.");
                    localStorage.setItem("token", response.data.token);
                    forgetShowSession();
                    // Redirect to home and force refresh to ensure app state reloads
                    window.location.href = returnPath;
                } else {
                    // Handle cases where API returns 200 but indicates failure (e.g., wrong password)
                    const errorMessage = response.data?.message || response.data?.error || "E-mail ou senha inválidos.";
                    console.warn("Login: Failed - API returned non-success:", errorMessage);
                    setError(errorMessage);
                }
            } catch (error) {
                console.error("Login: API Error:", error);
                let errorMessage = "Erro ao tentar fazer login. Tente novamente.";
                if (axios.isAxiosError(error) && error.response) {
                    // Extract specific error message from backend if available
                    errorMessage = error.response.data?.message || error.response.data?.error || `Erro ${error.response.status}: ${error.response.statusText}`;
                } else if (error instanceof Error) {
                    errorMessage = error.message;
                }
                setError(errorMessage);
            } finally {
                // Check mount? Less critical on Login screen usually
                setLoading(false);
            }
        };

        // Navigation handlers
        const handleSignup = () => {
            navigate("/signup", { state: location.state });
        };

        const handleForgotPassword = () => {
            navigate("/forgot-password");
        };

        // Handle Enter key press on password input
        const handlePasswordKeyPress = (e) => {
            if (e.key === 'Enter') {
                handleLogin();
            }
        };

        return (
            <div style={styles.container}>
                {/* Logo */}
                <img src={logo} alt="Logo A Próxima é Minha" style={styles.logo} />

                {/* App Name Added Below Logo */}
                <p style={styles.appName}>A Próxima é Minha</p>

                {/* Email Input */}
                <input
                    style={styles.input}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Seu e-mail"
                    aria-label="Email"
                    disabled={loading}
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="email" // Added autocomplete hint
                />

                {/* Password Input */}
                <input
                    style={styles.input}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={handlePasswordKeyPress} // Trigger login on Enter
                    placeholder="Sua senha"
                    aria-label="Senha"
                    disabled={loading}
                    autoComplete="current-password" // Added autocomplete hint
                />

                {/* Error Message Display */}
                {error && (
                    <div style={styles.errorBanner}>
                        <AlertCircle size={18} style={{ marginRight: '8px', flexShrink: 0 }}/>
                        <span>{error}</span>
                    </div>
                )}

                {/* Login Button */}
                <button style={styles.buttonPrimary(loading)} onClick={handleLogin} disabled={loading}>
                    {loading ? (
                        <>
                            <div style={styles.buttonSpinner}></div> Entrando...
                        </>
                    ) : (
                        <>
                            <LogIn size={18} /> Entrar
                        </>
                    )}
                </button>

                {/* Forgot Password Link */}
                <button style={styles.linkButton} onClick={handleForgotPassword} disabled={loading}>
                    Esqueci minha senha
                </button>

                {/* Signup Link */}
                <button style={styles.linkButton} onClick={handleSignup} disabled={loading}>
                    Não tem conta? <span style={styles.signupLink}>Cadastre-se</span>
                </button>

            </div>
        );
    }


    // --- Styles ---
    const styles = {
        container: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center", // Center vertically
            backgroundColor: "#12121f", // Dark background
            minHeight: "100vh", // Ensure full viewport height
            padding: '30px 20px', // Add more vertical padding
            boxSizing: 'border-box',
        },
        logo: {
            width: 'auto',
            height: 'auto',
            maxHeight: "100px",
            maxWidth: '80%',
            // marginBottom: 10, // Reduced margin below logo
            objectFit: "contain",
        },
        // Style for the App Name
        appName: {
            color: "#e0e0ff", // Lighter purple/white text
            fontSize: "18px",
            fontWeight: "600", // Semi-bold
            marginTop: "10px", // Space below logo
            marginBottom: "30px", // Space above inputs
            textAlign: "center",
            letterSpacing: "0.5px" // Optional: add letter spacing
        },
        input: {
            width: "100%", // Full width within padding
            maxWidth: "400px", // Max width for inputs
            padding: "14px 18px",
            margin: "8px 0", // Reduced margin between inputs
            border: "1px solid #4a4a6e", // Dark border
            borderRadius: "8px",
            backgroundColor: "#1a1a2e", // Dark input background
            color: "#f0f0f0", // Light text
            fontSize: "16px",
            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.3)", // Darker shadow
            boxSizing: 'border-box',
            outline: 'none',
            transition: 'border-color 0.2s ease',
            // ':focus': { borderColor: '#a0a0ff' } // Requires CSS libs
        },
        // Base button style
        buttonBase: {
            width: "100%",
            maxWidth: "400px",
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            padding: "12px",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: 'bold',
            border: "none",
            cursor: "pointer",
            transition: 'background-color 0.3s ease, opacity 0.3s ease',
            boxSizing: 'border-box',
        },
        // Primary button (Login) style, accepts loading state
        buttonPrimary: (isLoading) => ({
            ...styles.buttonBase, // Inherit base styles
            backgroundColor: isLoading ? '#5a0ca0' : '#6a0dad', // Theme purple, darker when loading
            color: "white",
            marginTop: 20, // Space above login button
            opacity: isLoading ? 0.7 : 1,
            cursor: isLoading ? "not-allowed" : "pointer",
        }),
        // Link-style button
        linkButton: {
            color: "#a0a0b0", // Light gray/purple link color
            background: "none",
            border: "none",
            marginTop: 15, // Space above links
            fontSize: 14,
            cursor: "pointer",
            padding: '5px', // Add padding for easier clicking
            textAlign: 'center',
            transition: 'color 0.2s ease',
            // ':hover': { color: '#c0c0ff' } // Requires CSS libs
        },
        signupLink: { // Specific style for the "Cadastre-se" part
            color: "#c0c0ff", // Brighter link color
            fontWeight: "bold",
            textDecoration: 'underline', // Optional underline
        },
        errorBanner: {
            width: '100%',
            maxWidth: "400px",
            backgroundColor: 'rgba(255, 96, 96, 0.1)',
            border: '1px solid rgba(255, 96, 96, 0.3)',
            color: '#ff8080',
            padding: '10px 15px',
            borderRadius: '8px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            textAlign: 'left',
            marginTop: '15px', // Space above error message
            marginBottom: '-5px', // Pull button closer if error shown
            boxSizing: 'border-box',
        },
        buttonSpinner: {
            border: '3px solid rgba(255, 255, 255, 0.3)',
            borderLeftColor: '#fff',
            borderRadius: '50%',
            width: '16px',
            height: '16px',
            animation: 'spin 1s linear infinite',
            display: 'inline-block',
        },
    };
